import React, { useCallback, useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { LoadingSpinner } from "../LoadingSpinner";
import { client } from "../../Clients";
import { objectDiffToSegments, type DiffSegment } from "../../Helper/yamlDiff";
import type { ContentViewModel } from "../../API/Models";

const PAGE_CONTENT_TYPE = "page";

/** Page: slug required. Singleton (Contact, Layout, Languages): contentType + language, no slug. */
export type DiffModalItem =
	| { id?: string; slug: string; language: string; title?: string | null }
	| { contentType: string; language: string; title?: string | null };

function isPageItem(item: DiffModalItem): item is { id?: string; slug: string; language: string; title?: string | null } {
	return "slug" in item && typeof (item as { slug?: string }).slug === "string";
}

function itemKey(item: DiffModalItem): string {
	if (isPageItem(item)) return `page:${item.language}/${item.slug}`;
	return `singleton:${item.contentType}/${item.language}`;
}

function itemLabel(item: DiffModalItem): string {
	const title = item.title ?? (isPageItem(item) ? item.slug : item.contentType);
	return `${title} (${item.language})`;
}

type CompareMode = "db-vs-preview" | "preview-vs-production";

	type DeploymentDiffModalProps = {
	show: boolean;
	onClose: () => void;
	/** Pages and/or singletons (Contact, Layout, Languages) to choose from */
	items: DiffModalItem[];
	/** When opening, preselect this item */
	initialItem?: DiffModalItem | null;
};

function getLeftRight(
	view: ContentViewModel | null,
	mode: CompareMode
): { left: unknown; right: unknown; leftLabel: string; rightLabel: string } {
	if (!view) return { left: {}, right: {}, leftLabel: "", rightLabel: "" };
	const current = view.current?.data ?? {};
	const preview = view.preview?.payload ?? null;
	const production = view.production?.payload ?? null;
	if (mode === "db-vs-preview") {
		return { left: current, right: preview ?? {}, leftLabel: "Base (actuel)", rightLabel: "Cloudflare Preview" };
	}
	return {
		left: preview ?? {},
		right: production ?? {},
		leftLabel: "Cloudflare Preview",
		rightLabel: "Cloudflare Production"
	};
}

/** Diff comparison modal between base, preview and production content versions. */
const DeploymentDiffModal: React.FC<DeploymentDiffModalProps> = ({
	show,
	onClose,
	items,
	initialItem
}) => {
	const [selected, setSelected] = useState<DiffModalItem | null>(null);
	const [compareMode, setCompareMode] = useState<CompareMode>("db-vs-preview");
	const [view, setView] = useState<ContentViewModel | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (show && initialItem) setSelected(initialItem);
		if (!show) setSelected(null);
	}, [show, initialItem]);

	const fetchView = useCallback(async (item: DiffModalItem) => {
		setLoading(true);
		try {
			if (isPageItem(item)) {
				const res = await client.content.apiContentSchemaKeyLanguageGet(
					PAGE_CONTENT_TYPE,
					item.language,
					item.slug,
					{ withCredentials: true, headers: { Accept: "application/json" } }
				);
				setView(res.data ?? null);
			} else {
				const res = await client.content.apiContentSchemaKeyLanguageGet(
					item.contentType,
					item.language,
					undefined,
					{ withCredentials: true, headers: { Accept: "application/json" } }
				);
				setView(res.data ?? null);
			}
		} catch (err) {
			console.error(err);
			setView(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (show && selected) {
			fetchView(selected);
		} else if (!show) {
			setSelected(null);
			setView(null);
		}
	}, [show, selected ? itemKey(selected) : null, fetchView]);

	const { left, right, leftLabel, rightLabel } = getLeftRight(view, compareMode);
	const segments: DiffSegment[] = view ? objectDiffToSegments(left, right) : [];

	if (!show) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
			<div
				className="bg-white rounded-2xl shadow-xl flex flex-col max-w-6xl w-full max-h-[90vh] overflow-hidden"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="shrink-0 flex items-center justify-between border-b border-slate-200 px-5 py-3">
					<h2 className="text-lg font-bold text-slate-900">View changes</h2>
					<button
						type="button"
						onClick={onClose}
						className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
						aria-label="Close"
					>
						<FiX className="h-5 w-5" />
					</button>
				</div>
				<div className="shrink-0 flex flex-wrap items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50/80">
					<label className="flex items-center gap-2 text-sm">
						<span className="text-slate-600 font-medium">Content</span>
						<select
							value={selected ? itemKey(selected) : ""}
							onChange={(e) => {
								const v = e.target.value;
								const item = items.find((i) => itemKey(i) === v);
								setSelected(item ?? null);
							}}
							className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 min-w-[200px]"
						>
							<option value="">Select content</option>
							{items.map((item) => (
								<option key={itemKey(item)} value={itemKey(item)}>
									{itemLabel(item)}
								</option>
							))}
						</select>
					</label>
					<label className="flex items-center gap-2 text-sm">
						<span className="text-slate-600 font-medium">Compare</span>
						<select
							value={compareMode}
							onChange={(e) => setCompareMode(e.target.value as CompareMode)}
							className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800"
						>
							<option value="db-vs-preview">Base vs Cloudflare Preview</option>
							<option value="preview-vs-production">Preview vs Production</option>
						</select>
					</label>
				</div>
				<div className="flex-1 min-h-0 overflow-auto p-4 flex gap-4">
					{!selected && (
						<p className="text-slate-500 text-sm">Select content to see the diff.</p>
					)}
					{selected && loading && (
						<div className="flex items-center gap-2 text-slate-500">
							<LoadingSpinner size="sm" /> Loading…
						</div>
					)}
					{selected && !loading && view && (
						<>
							<div className="flex-1 min-w-0 rounded-xl border border-slate-200 overflow-hidden flex flex-col">
								<div className="shrink-0 px-3 py-2 bg-slate-100 border-b border-slate-200 text-xs font-semibold text-slate-600">
									{leftLabel} — added green, unchanged neutral
								</div>
								<pre className="flex-1 overflow-auto p-3 text-xs font-mono whitespace-pre bg-slate-50/50 min-h-[200px]">
									{segments.map((seg, idx) => {
										const isAdded = seg.type === "removed";
										return (
											<div
												key={idx}
												className={
													isAdded
														? "bg-emerald-100 text-emerald-900 px-1 -mx-1"
														: "text-slate-700"
												}
											>
												{seg.left ?? "\u00A0"}
											</div>
										);
									})}
								</pre>
							</div>
							<div className="w-[1px] bg-slate-200 shrink-0" />
							<div className="flex-1 min-w-0 rounded-xl border border-slate-200 overflow-hidden flex flex-col">
								<div className="shrink-0 px-3 py-2 bg-slate-100 border-b border-slate-200 text-xs font-semibold text-slate-600">
									{rightLabel} — removed red, new green, edited orange
								</div>
								<pre className="flex-1 overflow-auto p-3 text-xs font-mono whitespace-pre bg-slate-50/50 min-h-[200px]">
									{segments.map((seg, idx) => {
										const prev = segments[idx - 1];
										const isRemovedFromRight = seg.type === "removed";
										const isEdited = seg.type === "added" && prev?.type === "removed";
										const isNew = seg.type === "added";
										return (
											<div
												key={idx}
												className={
													isRemovedFromRight
														? "text-slate-400"
														: isNew
															? isEdited
																? "bg-amber-100 text-amber-900 px-1 -mx-1"
																: "bg-emerald-100 text-emerald-900 px-1 -mx-1"
															: "text-slate-700"
												}
											>
												{isRemovedFromRight ? "\u00A0" : (seg.right ?? "\u00A0")}
											</div>
										);
									})}
								</pre>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default DeploymentDiffModal;
